import { useEffect, useMemo, useState } from 'react';

import { useSelectionParam } from '../../hooks/navigation/useUrlState';
import {
  useBulkHardDeleteRecipients,
  useBulkRemoveRecipients,
  useBulkRestoreRecipients,
  useRefreshMergeFields,
  useCampaignRecipients,
  useQueueRsvpManually,
  useRefreshListStatuses,
  useResumeFinishedPreview,
  useResumeFinishedRecipients,
} from '../../hooks/useOutreach';
import type {
  Campaign,
  CampaignRecipient,
  EnrollmentKind,
  RecipientStatus,
} from '../../types/outreach';
import { formatEtShort } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';
import { RecipientTimelineDrawer } from './RecipientTimelineDrawer';
import { RefreshButton } from './RefreshButton';
import { TestSendModal } from './TestSendModal';


type SortKey =
  | 'email' | 'first_name' | 'company' | 'status'
  | 'current_step' | 'current_list_status'
  | 'last_sent_at' | 'next_send_at';
type SortDir = 'asc' | 'desc';

interface ColumnFilters {
  // Single filter matching against email OR name OR company. Replaced
  // the three-input layout (May 2026 UX pass) since the merged
  // Recipient column shows all three together anyway.
  recipient: string;
  status: string;
  affinityStatus: string;
}

const EMPTY_FILTERS: ColumnFilters = {
  recipient: '', status: '', affinityStatus: '',
};

const RSVP_ONLY_PILL = '__rsvp_only';


// Statuses that mean "no more sends will go to this recipient." Used by
// the Next-step + Next-send columns to show "—" / "Complete" rather than
// stale numbers and zombie timestamps. Mirrors the sequencer's selector
// (which skips replied_at IS NOT NULL + suppression_snapshot=1) plus
// admin-applied terminal states.
const TERMINAL_STATUSES: ReadonlySet<RecipientStatus> = new Set<RecipientStatus>([
  'replied',
  'finished',
  'bounced',
  'unsubscribed',
  'suppressed',
  'paused_replied_elsewhere',
  'paused_manual',
  'removed',
  'errored',
]);

function isRecipientTerminal(r: CampaignRecipient): boolean {
  return TERMINAL_STATUSES.has(r.status);
}


// Per-org grouping mismatch: recipients sharing an email domain but
// belonging to distinct Affinity orgs (or some with an org + others
// with null). The engine batches by org_id, not by domain, so these
// won't be grouped at send time even if a reader of the recipients
// table assumes they will be.
interface DomainOrgWarning {
  domain: string;
  recipientCount: number;
  distinctOrgCount: number;
  sampleNames: string[];
}

function renderNextSend(r: CampaignRecipient): string {
  if (r.enrollment_kind === 'rsvp_only') return 'RSVP-only';
  if (isRecipientTerminal(r)) {
    // Show why the recipient is done so the operator doesn't have to
    // scan back to the Status column.
    if (r.status === 'replied') return 'Complete (replied)';
    if (r.status === 'finished') return 'Complete';
    if (r.status === 'bounced') return 'Complete (bounced)';
    if (r.status === 'unsubscribed') return 'Complete (unsubscribed)';
    if (r.status === 'suppressed') return 'Complete (suppressed)';
    if (r.status === 'paused_replied_elsewhere') return 'Complete (replied elsewhere)';
    if (r.status === 'paused_manual') return 'Paused';
    if (r.status === 'removed') return 'Removed';
    if (r.status === 'errored') return 'Errored';
    return 'Complete';
  }
  return r.next_send_at ? formatEtShort(r.next_send_at) : '—';
}


/**
 * Recipients sub-tab. Client-side sort + per-column filter + multiselect
 * + bulk actions on top of the (current) all-rows-fetched API. Works at
 * AGM scale (~hundreds); when any campaign exceeds ~500 recipients we'll
 * want to push sort/filter to the server.
 */
export function CampaignRecipientsTable({
  campaignId,
  campaign,
}: {
  campaignId: number;
  campaign?: Campaign;
}) {
  const [statusPill, setStatusPill] = useState<string>('');
  // statusPill drives the GET (server filters by status), filters/sort
  // are applied client-side over the returned page.
  const statusFilter = statusPill === RSVP_ONLY_PILL ? '' : statusPill;
  const enrollmentKindFilter =
    statusPill === RSVP_ONLY_PILL ? 'rsvp_only' : undefined;
  const rQ = useCampaignRecipients(
    campaignId, statusFilter, enrollmentKindFilter,
  );
  const queueRsvpMut = useQueueRsvpManually();

  const [filters, setFilters] = useState<ColumnFilters>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>('first_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [testSendFor, setTestSendFor] = useState<CampaignRecipient | null>(null);

  const eventConfigured =
    campaign?.event_starts_at != null && campaign?.event_ends_at != null;

  async function handleSendCal(r: CampaignRecipient) {
    const warning = eventConfigured ? '' : (
      '\n\nWarning: this campaign has no Event Details configured yet. '
      + 'The queued row will refuse to send until you fill in event '
      + 'date/time on the Overview tab.'
    );
    if (!window.confirm(
      `Queue a calendar invite for ${r.email}? An RSVP approval row will `
      + 'be created with action = Invite only; you approve it from the '
      + 'RSVP Queue tab.' + warning,
    )) return;
    try {
      await queueRsvpMut.mutateAsync({
        campaignId, recipientId: r.id, action: 'just_invite',
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'unknown';
      if (msg.includes('409') || /pending/i.test(msg)) {
        alert(
          'A pending RSVP approval already exists for this recipient. '
          + 'Approve or dismiss it from the RSVP Queue tab first.',
        );
      } else {
        alert(`Send Cal failed: ${msg}`);
      }
    }
  }
  // Timeline drawer is a drill-down deep link via `?recipient=<id>` (push —
  // Back closes the drawer). The recipient object is derived from the loaded
  // rows; if a deep-linked recipient is filtered out of the current view the
  // drawer simply won't open (acceptable — no single-recipient fetch endpoint).
  const { value: recipientParam, open: openRecipient, close: closeRecipient } =
    useSelectionParam('recipient');
  const drawerRecipientId =
    recipientParam != null && /^\d+$/.test(recipientParam) ? parseInt(recipientParam, 10) : null;
  const drawerFor =
    drawerRecipientId != null
      ? (rQ.data?.rows ?? []).find((r) => r.id === drawerRecipientId) ?? null
      : null;
  const setDrawerFor = (r: CampaignRecipient | null) => {
    if (r) openRecipient(String(r.id));
    else closeRecipient();
  };

  const removeMut = useBulkRemoveRecipients();
  const restoreMut = useBulkRestoreRecipients();
  const hardDeleteMut = useBulkHardDeleteRecipients();
  const refreshListStatusMut = useRefreshListStatuses();
  const refreshMergeMut = useRefreshMergeFields();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showResumeFinished, setShowResumeFinished] = useState(false);

  async function handleRefreshMergeFields() {
    try {
      const r = await refreshMergeMut.mutateAsync(campaignId);
      window.alert(r.message);
    } catch (e) {
      window.alert(
        `Refresh failed: ${e instanceof Error ? e.message : 'unknown'}`,
      );
    }
  }

  async function handleRefreshListStatuses() {
    try {
      const res = await refreshListStatusMut.mutateAsync(campaignId);
      const total = res.updated + res.unchanged + res.missing + res.errors;
      alert(
        `List statuses refreshed (${total} recipients):\n` +
        `  Updated: ${res.updated}\n` +
        `  Already in sync: ${res.unchanged}\n` +
        `  Missing on the source list: ${res.missing}\n` +
        `  Errors: ${res.errors}`,
      );
    } catch (e: unknown) {
      alert(
        `Refresh failed: ${e instanceof Error ? e.message : 'unknown'}`,
      );
    }
  }

  const data = rQ.data;

  const filteredSorted = useMemo(() => {
    if (!data) return [];
    const norm = (s: string) => s.trim().toLowerCase();
    const matchText = (val: string | null, q: string) =>
      !q || (val || '').toLowerCase().includes(norm(q));
    const filtered = data.rows.filter((r) => {
      const q = filters.recipient;
      const matchesRecipient =
        !q
        || matchText(r.email, q)
        || matchText(
          [r.first_name, r.last_name].filter(Boolean).join(' '), q,
        )
        || matchText(r.company, q);
      return matchesRecipient
        && (!filters.status || r.status === filters.status)
        && (
          !filters.affinityStatus
          || (r.current_list_status || '') === filters.affinityStatus
        );
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    const key = sortKey;
    const sortVal = (r: CampaignRecipient): string | number => {
      switch (key) {
        case 'email':                return (r.email || '').toLowerCase();
        case 'first_name':           return (r.first_name || '').toLowerCase();
        case 'company':              return (r.company || '').toLowerCase();
        case 'status':               return r.status;
        case 'current_step':         return r.current_step;
        case 'current_list_status':  return (r.current_list_status || '').toLowerCase();
        case 'last_sent_at':
          // Recipients with no sends sort to the END regardless of dir
          // by mapping null -> +Infinity. (Inverse of next_send_at:
          // there, "no scheduled send" is also +Infinity, so unsent +
          // unscheduled rows cluster together at the bottom.)
          return r.last_sent_at ? new Date(r.last_sent_at).getTime() : Infinity;
        case 'next_send_at':
          return r.next_send_at ? new Date(r.next_send_at).getTime() : Infinity;
      }
    };
    return [...filtered].sort((a, b) => {
      const va = sortVal(a);
      const vb = sortVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [data, filters, sortKey, sortDir]);

  // Per-org domain-vs-org warnings. The sequencer groups recipients by
  // affinity_org_id (or a person:* singleton when org_id is null), not
  // by email domain — so a campaign author who eyeballs "five @foo.com
  // addresses" expecting one batched send can be surprised when the
  // engine splits them into multiple sends because Affinity has them
  // tagged to different organizations (or some are tagged + others
  // aren't). Surface the mismatched domains here as a warning banner.
  // Per-person campaigns skip this entirely (grouping is irrelevant).
  const domainOrgWarnings = useMemo(() => {
    if (!data) return [] as DomainOrgWarning[];
    if (campaign?.send_mode !== 'per_organization') return [];
    type Bucket = { domain: string; orgIds: Set<string>; rows: CampaignRecipient[] };
    const byDomain = new Map<string, Bucket>();
    for (const r of data.rows) {
      const at = r.email.indexOf('@');
      if (at < 0) continue;
      const domain = r.email.slice(at + 1).toLowerCase();
      if (!domain) continue;
      let b = byDomain.get(domain);
      if (!b) {
        b = { domain, orgIds: new Set<string>(), rows: [] };
        byDomain.set(domain, b);
      }
      // Stringify so null + a real org_id register as distinct keys.
      b.orgIds.add(r.affinity_org_id == null ? 'null' : String(r.affinity_org_id));
      b.rows.push(r);
    }
    const warnings: DomainOrgWarning[] = [];
    for (const b of byDomain.values()) {
      if (b.rows.length < 2 || b.orgIds.size < 2) continue;
      // Render at most 3 sample recipient labels per domain — keeps the
      // banner scannable when a domain has a dozen mismatches.
      const samples = b.rows.slice(0, 3).map((r) =>
        [r.first_name, r.last_name].filter(Boolean).join(' ').trim() || r.email
      );
      warnings.push({
        domain: b.domain,
        recipientCount: b.rows.length,
        distinctOrgCount: b.orgIds.size,
        sampleNames: samples,
      });
    }
    warnings.sort((a, b) => b.recipientCount - a.recipientCount);
    return warnings;
  }, [data, campaign?.send_mode]);

  // Pull together unique values for the dropdown filters.
  const statusOptions = useMemo(() => {
    if (!data) return [] as string[];
    return Array.from(new Set(data.rows.map((r) => r.status))).sort();
  }, [data]);
  const affinityStatusOptions = useMemo(() => {
    if (!data) return [] as string[];
    return Array.from(
      new Set(
        data.rows
          .map((r) => r.current_list_status)
          .filter((s): s is string => !!s),
      ),
    ).sort();
  }, [data]);

  if (rQ.isLoading) return <div className={styles.empty}>Loading…</div>;
  if (rQ.isError) {
    // Surface the actual error rather than masking it as "No recipients."
    // The original fallback was indistinguishable from the empty state and
    // hid a T-SQL 500 in prod (May 2026).
    const msg = rQ.error instanceof Error ? rQ.error.message : 'unknown error';
    return (
      <div className={styles.empty} style={{ color: '#B04030' }}>
        Failed to load recipients: {msg}
      </div>
    );
  }
  if (!data) return <div className={styles.empty}>No recipients.</div>;

  const filterPills: { label: string; value: string }[] = [
    { label: `All (${data.total})`, value: '' },
    ...Object.entries(data.by_status)
      .filter(([k]) => k !== 'removed')
      .map(([k, n]) => ({ label: `${k} (${n})`, value: k })),
  ];
  const rsvpOnlyCount = data.by_enrollment_kind?.rsvp_only || 0;
  if (rsvpOnlyCount > 0) {
    filterPills.push({
      label: `RSVP-only (${rsvpOnlyCount})`,
      value: RSVP_ONLY_PILL,
    });
  }
  const removedCount = data.by_status['removed'] || 0;
  if (removedCount > 0) {
    filterPills.push({ label: `removed (${removedCount})`, value: 'removed' });
  }

  const isViewingRemoved = statusPill === 'removed';
  const allVisibleSelected =
    filteredSorted.length > 0
    && filteredSorted.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir('asc');
    }
  }

  function toggleAll() {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredSorted.map((r) => r.id)));
    }
  }

  function toggleOne(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleBulkRemove() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Remove ${ids.length} recipient(s)? They'll be soft-removed (status='removed') and the sequencer will skip them. Reversible from the "removed" filter.`,
    )) return;
    try {
      const r = await removeMut.mutateAsync({
        campaignId, recipientIds: ids,
      });
      alert(r.message || `Removed ${r.affected}.`);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      alert(`Remove failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  async function handleBulkRestore() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const r = await restoreMut.mutateAsync({
        campaignId, recipientIds: ids,
      });
      alert(r.message || `Restored ${r.affected}.`);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      alert(`Restore failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  async function handleConfirmClear(targetIds: number[]) {
    try {
      const r = await hardDeleteMut.mutateAsync({
        campaignId, recipientIds: targetIds,
      });
      // Surface refusals so the operator knows why some were skipped
      // (replied / in-flight) rather than silently coming up short.
      if (r.refused.length > 0) {
        const reasons = r.refused.reduce<Record<string, number>>((acc, x) => {
          acc[x.reason] = (acc[x.reason] || 0) + 1;
          return acc;
        }, {});
        const reasonLabel = (k: string) =>
          k === 'has_reply' ? 'has reply'
          : k === 'has_in_flight_send' ? 'send in flight'
          : k;
        const summary = Object.entries(reasons)
          .map(([k, n]) => `${n} ${reasonLabel(k)}`).join(', ');
        alert(
          `Deleted ${r.deleted}. Refused ${r.refused.length} (${summary}).\n\n` +
          'Restore + resolve those recipients before retrying.',
        );
      } else {
        alert(r.message || `Deleted ${r.deleted}.`);
      }
      setSelectedIds(new Set());
      setShowClearConfirm(false);
    } catch (e: unknown) {
      alert(`Clear failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {domainOrgWarnings.length > 0 && (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            border: '1px solid #E0C070',
            background: '#FFF8E1',
            borderRadius: 6,
            fontSize: 12,
            color: '#5A4A1A',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Heads up: per-organization grouping won&apos;t batch these
          </div>
          <div style={{ marginBottom: 6 }}>
            Recipients sharing a domain but tagged to different
            organizations (or some with no org) get separate sends — the
            engine groups by organization, not by email domain.
            Fix by consolidating the orgs on the source list, or switch
            the campaign to per_person send mode.
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {domainOrgWarnings.map((w) => (
              <li key={w.domain} style={{ marginBottom: 2 }}>
                <strong>@{w.domain}</strong> — {w.recipientCount} recipients
                across {w.distinctOrgCount} orgs ({w.sampleNames.join(', ')}
                {w.recipientCount > w.sampleNames.length ? ', …' : ''})
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Status filter pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {filterPills.map((p) => (
          <button
            key={p.value || 'all'}
            type="button"
            className={`${styles.button} ${statusPill === p.value ? styles.buttonPrimary : ''}`}
            onClick={() => {
              setStatusPill(p.value);
              setSelectedIds(new Set());
              // Bug fix: the column-level status dropdown filter
              // (filters.status) is independent of the pill. If the
              // user touched the column dropdown earlier, that value
              // persists across pill clicks -- which silently filtered
              // out the new pill's rows. e.g. column filter='pending'
              // + click 'removed' pill -> rows have status='removed'
              // -> dropped client-side -> "Showing 0 of 1" even though
              // the badge says 1. Clearing on pill change makes the
              // pill the single source of truth for the status axis.
              setFilters((f) => ({ ...f, status: '' }));
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      <div
        style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 12, color: '#666' }}>
          Showing {filteredSorted.length} of {data.rows.length}
          {someSelected && ` · ${selectedIds.size} selected`}
        </span>
        <RefreshButton
          onClick={() => rQ.refetch()}
          isFetching={rQ.isFetching}
          title="Re-fetch recipients from the server"
        />
        <button
          type="button"
          className={styles.button}
          onClick={handleRefreshListStatuses}
          disabled={refreshListStatusMut.isPending}
          title={
            'Pull live status from the source list for every recipient ' +
            'and update the local mirror. Use to backfill drift or pick ' +
            'up manual edits made outside the engine.'
          }
        >
          {refreshListStatusMut.isPending ? 'Syncing…' : 'Sync status'}
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={handleRefreshMergeFields}
          disabled={refreshMergeMut.isPending}
          title={
            'Re-pull each non-terminal recipient\'s source-list field ' +
            'values and update their merge_fields snapshot. Use after ' +
            'adding a new ' +
            'list-scoped custom merge field so existing recipients pick up ' +
            'the value without re-enrollment.'
          }
        >
          {refreshMergeMut.isPending ? 'Refreshing…' : 'Refresh merge fields'}
        </button>
        <div style={{ flex: 1 }} />
        {(data.by_status['finished'] || 0) > 0 && !isViewingRemoved
          && !campaign?.archived_at && (
          <button
            type="button"
            className={styles.button}
            onClick={() => setShowResumeFinished(true)}
            title={
              'Resume finished recipients into the next step that already '
              + 'exists beyond where they stopped (e.g. they finished after '
              + 'step 1 and you later added step 2). Anchors the next send on '
              + 'their last send + the new step\'s delay.'
            }
          >
            {someSelected
              ? `Resume from next step (${selectedIds.size} selected)`
              : 'Resume from next step'}
          </button>
        )}
        {someSelected && !isViewingRemoved && (
          <button
            type="button"
            className={`${styles.button} ${styles.buttonDanger}`}
            onClick={handleBulkRemove}
            disabled={removeMut.isPending}
          >
            {removeMut.isPending
              ? 'Removing…'
              : `Remove selected (${selectedIds.size})`}
          </button>
        )}
        {someSelected && isViewingRemoved && (
          <button
            type="button"
            className={styles.button}
            onClick={handleBulkRestore}
            disabled={restoreMut.isPending}
          >
            {restoreMut.isPending
              ? 'Restoring…'
              : `Restore selected (${selectedIds.size})`}
          </button>
        )}
        {/* Contextual hard-delete button:
              N selected -> "Clear Selected (N)"
              No selection but pill/column filters narrow the visible
              set -> "Clear Filtered (N)"
              Otherwise -> "Clear All (N)"
            All three call POST /recipients/bulk-hard-delete with the
            ids of the currently-targeted set. */}
        {(() => {
          const isFilterActive = !!(
            statusPill
            || filters.recipient
            || filters.status
            || filters.affinityStatus
          );
          const targets = someSelected
            ? data.rows.filter((r) => selectedIds.has(r.id))
            : filteredSorted;
          const targetIds = targets.map((r) => r.id);
          const label = someSelected
            ? `Clear Selected (${targets.length})`
            : isFilterActive
              ? `Clear Filtered (${targets.length})`
              : `Clear All (${targets.length})`;
          return (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={() => setShowClearConfirm(true)}
              disabled={hardDeleteMut.isPending || targetIds.length === 0}
              title={
                someSelected
                  ? 'Hard-delete the selected recipients'
                  : isFilterActive
                    ? 'Hard-delete every recipient matching the current filters/pill'
                    : 'Hard-delete every recipient on this campaign'
              }
            >
              {hardDeleteMut.isPending ? 'Clearing…' : label}
            </button>
          );
        })()}
      </div>

      {filteredSorted.length === 0 ? (
        <div className={styles.empty}>
        {data.rows.length === 0
          ? statusPill
              ? statusPill === RSVP_ONLY_PILL
                ? 'No RSVP-only recipients.'
                : `No recipients in status "${statusPill}".`
              : 'No recipients enrolled yet.'
            : 'No recipients match the current filters.'}
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAll}
                  aria-label="Select all visible"
                />
              </th>
              <SortHeader
                label="Recipient"
                k="first_name"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <SortHeader
                label="Status"
                k="status"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <SortHeader
                label="List status"
                k="current_list_status"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <SortHeader
                label="Last sent"
                k="last_sent_at"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <th
                style={{ textAlign: 'right' }}
                title="Tracked image loads across this recipient's sends (estimated; may include provider prefetch/caching)"
              >
                Opens
              </th>
              <th
                style={{ textAlign: 'right' }}
                title="Tracked link clicks across this recipient's sends"
              >
                Clicks
              </th>
              <th title="Whether this recipient has replied">Replied</th>
              <th title="Latest RSVP form/link response">RSVP</th>
              <SortHeader
                label="Next step"
                k="current_step"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <SortHeader
                label="Next send"
                k="next_send_at"
                {...{ sortKey, sortDir, toggleSort }}
              />
              <th style={{ width: 100 }}>Actions</th>
            </tr>
            <tr>
              <th />
              <th>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="filter name / email / company…"
                  value={filters.recipient}
                  onChange={(e) =>
                    setFilters({ ...filters, recipient: e.target.value })
                  }
                  style={{ width: '100%', fontSize: 11, padding: '3px 6px' }}
                />
              </th>
              <th>
                <select
                  className={styles.input}
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                  style={{ width: '100%', fontSize: 11, padding: '2px 4px' }}
                >
                  <option value="">all</option>
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  className={styles.input}
                  value={filters.affinityStatus}
                  onChange={(e) =>
                    setFilters({ ...filters, affinityStatus: e.target.value })
                  }
                  style={{ width: '100%', fontSize: 11, padding: '2px 4px' }}
                >
                  <option value="">all</option>
                  {affinityStatusOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </th>
              {/* Last sent, Opens, Clicks, Replied, RSVP, Next step, Next send, Actions */}
              <th />
              <th />
              <th />
              <th />
              <th />
              <th />
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredSorted.map((r) => (
              <RecipientRow
                key={r.id}
                r={r}
                campaign={campaign}
                selected={selectedIds.has(r.id)}
                onToggle={() => toggleOne(r.id)}
                onSendTest={() => setTestSendFor(r)}
                onSendCal={() => handleSendCal(r)}
                onOpenTimeline={() => setDrawerFor(r)}
              />
            ))}
          </tbody>
        </table>
      )}

      {testSendFor && (
        <TestSendModal
          campaignId={campaignId}
          recipient={testSendFor}
          onClose={() => setTestSendFor(null)}
        />
      )}
      {drawerFor && (
        <RecipientTimelineDrawer
          campaignId={campaignId}
          recipient={drawerFor}
          onClose={() => setDrawerFor(null)}
        />
      )}
      {showClearConfirm && (() => {
        // Recompute targets at modal-render time so filter / selection
        // changes between button click and confirm reflect.
        const isFilterActive = !!(
          statusPill
          || filters.recipient
          || filters.status
          || filters.affinityStatus
        );
        const targets = someSelected
          ? data.rows.filter((r) => selectedIds.has(r.id))
          : filteredSorted;
        const scope = someSelected
          ? 'selected'
          : isFilterActive ? 'filtered' : 'all';
        return (
          <ClearRecipientsConfirmModal
            targets={targets}
            scope={scope}
            isPending={hardDeleteMut.isPending}
            onCancel={() => setShowClearConfirm(false)}
            onConfirm={() => handleConfirmClear(targets.map((r) => r.id))}
          />
        );
      })()}
      {showResumeFinished && (
        <ResumeFinishedModal
          campaignId={campaignId}
          // When rows are selected, scope to them (backend keeps only the
          // finished ones); otherwise resume ALL finished recipients.
          recipientIds={someSelected ? Array.from(selectedIds) : undefined}
          onClose={() => setShowResumeFinished(false)}
          onDone={() => {
            setShowResumeFinished(false);
            setSelectedIds(new Set());
          }}
        />
      )}
    </div>
  );
}


/**
 * Confirm modal for the "Resume from next step" bulk action. Fires the
 * read-only preview on mount to show the exact eligible count + forecast
 * before the operator commits. Confirm flips the finished recipients back
 * to pending so the sequencer sends them the next existing step.
 */
function ResumeFinishedModal({
  campaignId, recipientIds, onClose, onDone,
}: {
  campaignId: number;
  recipientIds?: number[];
  onClose: () => void;
  onDone: () => void;
}) {
  const previewMut = useResumeFinishedPreview();
  const resumeMut = useResumeFinishedRecipients();
  const { mutate: runPreview } = previewMut;

  // Snapshot the selection once on open. The modal is launched against a
  // fixed selection, but `recipientIds` arrives as a fresh array each parent
  // render and the parent polls recipients every 30s — keying the effect on
  // the live prop would re-fire the preview POST (and flicker the count) on
  // every poll. Both preview and execute use the snapshot so they target the
  // identical set.
  const [snapshotIds] = useState(recipientIds);

  useEffect(() => {
    runPreview({ campaignId, recipientIds: snapshotIds });
  }, [runPreview, campaignId, snapshotIds]);

  const impact = previewMut.data;
  const scopeWord = snapshotIds ? `${snapshotIds.length} selected` : 'all finished';

  async function confirm() {
    try {
      const r = await resumeMut.mutateAsync({ campaignId, recipientIds: snapshotIds });
      const parts = [
        `Resumed ${r.reactivated_count} recipient`
          + `${r.reactivated_count === 1 ? '' : 's'} into their next step.`,
      ];
      if (r.sends_blocked_by_campaign_status) {
        parts.push(
          'Campaign is paused/finished — sends will not fire until you '
          + 'reactivate the campaign.',
        );
      }
      alert(parts.join(' '));
      onDone();
    } catch (e: unknown) {
      alert(`Resume failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF', padding: 24, borderRadius: 8,
          minWidth: 460, maxWidth: 560, fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>
          Resume {scopeWord} recipient(s) from their next step?
        </h3>
        {previewMut.isPending && (
          <div style={{ fontSize: 13, color: '#666' }}>Calculating…</div>
        )}
        {previewMut.isError && (
          <div className={styles.error}>
            Preview failed: {previewMut.error instanceof Error
              ? previewMut.error.message : 'unknown'}
          </div>
        )}
        {impact && (
          <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6 }}>
            <div>
              <strong>{impact.eligible}</strong> finished recipient
              {impact.eligible === 1 ? '' : 's'} will be flipped to{' '}
              <code>pending</code> and sent their next existing step.
            </div>
            {impact.eligible > 0 && impact.earliest_next_send_at && (
              <div style={{ marginTop: 6, color: '#666' }}>
                Next sends land between{' '}
                {formatEtShort(impact.earliest_next_send_at)} and{' '}
                {impact.latest_next_send_at
                  ? formatEtShort(impact.latest_next_send_at) : '—'}.
              </div>
            )}
            {impact.skipped_no_next_step > 0 && (
              <div style={{ marginTop: 6, color: '#666' }}>
                {impact.skipped_no_next_step} skipped — already at the last step.
              </div>
            )}
            {impact.skipped_no_prior_send > 0 && (
              <div style={{ marginTop: 6, color: '#666' }}>
                {impact.skipped_no_prior_send} skipped — no prior send to anchor on.
              </div>
            )}
            {impact.sends_blocked_by_campaign_status && (
              <div
                style={{
                  marginTop: 10, padding: '8px 12px', borderRadius: 6,
                  background: '#FFF7E0', border: '1px solid #E0C070',
                  color: '#7B5400', fontSize: 12,
                }}
              >
                Campaign isn&apos;t active — recipients flip to pending but
                nothing sends until you reactivate the campaign.
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" className={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={confirm}
            disabled={resumeMut.isPending || !impact || impact.eligible === 0}
          >
            {resumeMut.isPending
              ? 'Resuming…'
              : `Resume ${impact?.eligible ?? ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Two-step confirm for the unified Clear button. Shows the count of
 * affected recipients + a history breakdown so the operator knows
 * exactly what's being lost (sends / replies vs. clean pending rows).
 *
 * "scope" wording carries the contextual label from the button into
 * the headline so the operator never confuses Selected with Filtered
 * with All -- they look identical mid-confirm if you only show counts.
 */
function ClearRecipientsConfirmModal({
  targets, scope, isPending, onCancel, onConfirm,
}: {
  targets: CampaignRecipient[];
  scope: 'selected' | 'filtered' | 'all';
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);

  const counts = useMemo(() => {
    let withSends = 0;
    let withReplies = 0;
    let blocked = 0;  // can't be deleted (replied OR in-flight)
    for (const r of targets) {
      const sentSomething = r.last_sent_at != null || r.current_step > 0;
      const replied = r.replied_at != null;
      if (sentSomething) withSends += 1;
      if (replied) withReplies += 1;
      // Replied rows always refused. (Backend also refuses in_flight,
      // but we don't have per-recipient send_status here -- the
      // refusal will come back from the API.)
      if (replied) blocked += 1;
    }
    const noActivity = targets.length - withSends;
    return { withSends, withReplies, blocked, noActivity };
  }, [targets]);

  const scopeWord = scope === 'selected'
    ? 'selected'
    : scope === 'filtered' ? 'currently filtered' : 'all';

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFF', padding: 24, borderRadius: 8,
          minWidth: 480, maxWidth: 600,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {step === 1 ? (
          <>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>
              Hard-delete {targets.length} {scopeWord} recipient
              {targets.length === 1 ? '' : 's'}?
            </h3>
            <div style={{ fontSize: 13, color: '#444', lineHeight: 1.55 }}>
              {counts.withSends > 0 ? (
                <div
                  style={{
                    background: '#FFF7E0', border: '1px solid #F0D070',
                    color: '#7B5400', padding: '10px 12px', borderRadius: 6,
                    marginBottom: 12,
                  }}
                >
                  <strong>⚠ Send / reply history will be permanently deleted</strong>
                  <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12 }}>
                    <li>{counts.withSends} have sent emails (campaign_sends rows removed)</li>
                    {counts.withReplies > 0 && (
                      <li>{counts.withReplies} have replies on file (campaign_replies rows removed)</li>
                    )}
                  </ul>
                </div>
              ) : null}
              {counts.noActivity > 0 ? (
                <div style={{ marginBottom: 12 }}>
                  {counts.noActivity} {counts.noActivity === 1 ? 'is' : 'are'} pending
                  with no activity yet.
                </div>
              ) : null}
              {counts.blocked > 0 ? (
                <div
                  style={{
                    background: '#FBEBD8', border: '1px solid #E0A060',
                    color: '#7B3500', padding: '10px 12px', borderRadius: 6,
                    fontSize: 12, marginBottom: 12,
                  }}
                >
                  <strong>{counts.blocked}</strong> recipient
                  {counts.blocked === 1 ? '' : 's'} cannot be deleted (have
                  replies). The API will refuse those and return a
                  reason; the rest go through.
                </div>
              ) : null}
              <div style={{ fontSize: 12, color: '#666' }}>
                List status fields stay untouched. This cannot be
                undone.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button type="button" className={styles.button} onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonDanger}`}
                onClick={() => setStep(2)}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>
              Are you sure?
            </h3>
            <div style={{ fontSize: 13, color: '#444', marginBottom: 16 }}>
              This will hard-delete {targets.length} recipient
              {targets.length === 1 ? '' : 's'} and their entire
              send / reply / response history on this campaign.
              <strong> This cannot be undone.</strong>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className={styles.button} onClick={() => setStep(1)}>
                Back
              </button>
              <button
                type="button"
                className={`${styles.button} ${styles.buttonDanger}`}
                onClick={onConfirm}
                disabled={isPending}
              >
                {isPending ? 'Deleting…' : `Delete ${targets.length}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function SortHeader({
  label, k, sortKey, sortDir, toggleSort,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  toggleSort: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      onClick={() => toggleSort(k)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      {active && (
        <span style={{ marginLeft: 4, fontSize: 9, color: '#888' }}>
          {arrow}
        </span>
      )}
    </th>
  );
}


function RecipientRow({
  r, campaign, selected, onToggle, onSendTest, onSendCal, onOpenTimeline,
}: {
  r: CampaignRecipient;
  campaign?: Campaign;
  selected: boolean;
  onToggle: () => void;
  onSendTest: () => void;
  onSendCal: () => void;
  onOpenTimeline: () => void;
}) {
  const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
  // "—" (not 0) when the campaign never enabled tracking, so an untracked
  // campaign doesn't read as "zero opens/clicks". track_* default to
  // undefined on older Campaign payloads -> treat as untracked.
  const opensCell = campaign?.track_opens ? r.open_count : '—';
  const clicksCell = campaign?.track_clicks ? r.click_count : '—';
  const repliedCell = r.replied_at ? 'Yes' : 'No';
  const rsvpCell =
    r.rsvp_response === 'yes' ? 'Yes'
      : r.rsvp_response === 'no' ? 'No'
        : '—';
  return (
    <tr style={selected ? { background: '#FFF7E0' } : undefined}>
      <td onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onToggle} />
      </td>
      <td>
        <button
          type="button"
          onClick={onOpenTimeline}
          style={{
            background: 'none', border: 0, padding: 0, textAlign: 'left',
            cursor: 'pointer', font: 'inherit',
          }}
          title="Open timeline drawer for this recipient"
        >
          <div style={{ fontSize: 13, color: '#1a4f8a' }}>
            {name || r.email}
            {r.enrollment_kind === 'rsvp_only' && (
              <EnrollmentKindBadge kind={r.enrollment_kind} />
            )}
          </div>
          <div
            className={styles.helpText}
            style={{ fontSize: 12, marginTop: 1 }}
          >
            {name ? r.email : ''}
            {name && r.company ? ' · ' : ''}
            {r.company || ''}
          </div>
        </button>
      </td>
      <td>
        <RecipientStatusBadge
          status={r.status}
          rsvpResponse={r.rsvp_response}
        />
      </td>
      <td className={styles.code}>{r.current_list_status || '—'}</td>
      <td>
        {r.last_sent_at ? formatEtShort(r.last_sent_at) : '—'}
      </td>
      <td style={{ textAlign: 'right' }} className={styles.code}>{opensCell}</td>
      <td style={{ textAlign: 'right' }} className={styles.code}>{clicksCell}</td>
      <td>
        <EngagementYesNo value={repliedCell} />
      </td>
      <td>
        <EngagementYesNo value={rsvpCell} />
      </td>
      <td>
        {/* Display the upcoming step rather than the last completed
            one — matches the "Next step" column header. */}
        {r.enrollment_kind === 'rsvp_only'
          ? '—'
          : isRecipientTerminal(r)
            ? '—'
            : r.current_step + 1}
      </td>
      <td>
        {renderNextSend(r)}
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4, whiteSpace: 'nowrap' }}>
          <button
            type="button"
            className={styles.button}
            onClick={onSendTest}
            title="Send a one-off test of this campaign's step 1 to a chosen internal address"
            style={{ fontSize: 11, padding: '3px 8px' }}
          >
            Send test
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={onSendCal}
            title="Queue a calendar-invite-only RSVP approval for this recipient"
            style={{ fontSize: 11, padding: '3px 8px' }}
          >
            Send cal
          </button>
        </div>
      </td>
    </tr>
  );
}


// Compact Yes/No/— pill for the Replied + RSVP columns. Yes = green,
// No = muted neutral, — = absent (no reply / no RSVP on file).
function EngagementYesNo({ value }: { value: 'Yes' | 'No' | '—' }) {
  if (value === '—') return <span style={{ color: '#999' }}>—</span>;
  const yes = value === 'Yes';
  return (
    <span
      style={{
        display: 'inline-block', padding: '1px 7px', borderRadius: 9,
        background: yes ? '#E5F4E5' : '#F0F0EC',
        color: yes ? '#2E6E2E' : '#777',
        fontSize: 11, fontFamily: "'DM Mono', monospace",
      }}
    >
      {value}
    </span>
  );
}


export function EnrollmentKindBadge({ kind }: { kind: EnrollmentKind }) {
  if (kind !== 'rsvp_only') return null;
  return (
    <span
      style={{
        display: 'inline-block',
        marginLeft: 6,
        padding: '1px 6px',
        borderRadius: 8,
        background: '#FFF7E0',
        color: '#7B5400',
        border: '1px solid #E8D69A',
        fontSize: 10,
        fontFamily: "'DM Mono', monospace",
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        verticalAlign: 'middle',
      }}
      title="RSVP/form only. This recipient is excluded from campaign email sends."
    >
      RSVP-only
    </span>
  );
}


export function RecipientStatusBadge({
  status,
  rsvpResponse,
}: {
  status: RecipientStatus;
  rsvpResponse?: 'yes' | 'no' | null;
}) {
  // When status is `replied`, the recipient could have:
  //   (a) submitted the RSVP form Yes  -> show "RSVP YES"  (green)
  //   (b) submitted the RSVP form No   -> show "RSVP NO"   (muted red)
  //   (c) replied via email (no form)  -> show "REPLIED"   (green)
  // The discriminator is `rsvp_response`, derived from the latest
  // campaign_responses row at the API layer.
  let displayLabel: string = status.replace(/_/g, ' ');
  let bg = '#F0F0EC';
  let fg = '#666';

  const colorMap: Partial<Record<RecipientStatus, { bg: string; fg: string }>> = {
    pending:                  { bg: '#F0F0EC', fg: '#666' },
    in_progress:              { bg: '#E5E8F4', fg: '#2A4A8A' },
    throttled:                { bg: '#FFF7E0', fg: '#7B5400' },
    replied:                  { bg: '#E5F4E5', fg: '#2E6E2E' },
    bounced:                  { bg: '#FCEFEC', fg: '#B04030' },
    unsubscribed:             { bg: '#FCEFEC', fg: '#B04030' },
    suppressed:               { bg: '#FCEFEC', fg: '#B04030' },
    paused_manual:            { bg: '#F5E5F4', fg: '#6A2A6A' },
    paused_replied_elsewhere: { bg: '#F5E5F4', fg: '#6A2A6A' },
    finished:                 { bg: '#E5E8F4', fg: '#2A4A8A' },
    errored:                  { bg: '#FCEFEC', fg: '#B04030' },
    removed:                  { bg: '#F0F0EC', fg: '#999' },
  };
  const fallback = colorMap[status] || { bg: '#F0F0EC', fg: '#666' };
  bg = fallback.bg;
  fg = fallback.fg;

  if (status === 'replied' && rsvpResponse === 'yes') {
    displayLabel = 'RSVP YES';
    // Brighter green than vanilla replied so it pops on a list of
    // mostly-replied rows during the AGM week.
    bg = '#D5EFD3';
    fg = '#1F5E1F';
  } else if (status === 'replied' && rsvpResponse === 'no') {
    displayLabel = 'RSVP NO';
    bg = '#F4DEDA';
    fg = '#8A2A1F';
  }

  return (
    <span
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
        background: bg, color: fg, fontSize: 11,
        fontFamily: "'DM Mono', monospace",
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}
    >
      {displayLabel}
    </span>
  );
}
